import { z } from 'zod';
import { ReaderInterface } from '@am3nd-org/pointcraft-stripe-terminal';
import {
  AllPaymentProvidersEnum,
  BusinessDeviceItemSchema,
  BusinessTerminalItemSchema,
} from '@am3nd-org/pointcraft-types';

import { AppNameEnum } from 'src/sections/app-hub/types';

import type {
  UpdatePolicyState,
  GlobalUpdatePolicy,
  MergedUpdatePolicy,
  BusinessUpdatePolicy,
} from 'src/types/update-policy';

export enum DeviceType {
  MOBILE = 'MOBILE',
  TABLET = 'TABLET',
  DESKTOP = 'DESKTOP',
  COMPANION = 'COMPANION',
}

export enum BleReaderStatus {
  NOT_INITIALIZED = 'NOT_INITIALIZED',
  INITIALIZING = 'INITIALIZING',
  INITIALIZED = 'INITIALIZED',
  DISCOVERING = 'DISCOVERING',
  CONNECTING = 'CONNECTING',
  CONNECTED = 'CONNECTED',
  COLLECTING_PAYMENT = 'COLLECTING_PAYMENT',
  PROCESSING_PAYMENT = 'PROCESSING_PAYMENT',
  PAYMENT_COLLECTED = 'PAYMENT_COLLECTED',
  DISCONNECTED = 'DISCONNECTED',
  ERROR = 'ERROR',
}

export enum BlePaymentStatus {
  IDLE = 'IDLE',
  COLLECTING = 'COLLECTING',
  COLLECTED = 'COLLECTED',
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
}

export enum TerminalConnectionStatus {
  DISCONNECTED = 'DISCONNECTED',
  CONNECTING = 'CONNECTING',
  CONNECTED = 'CONNECTED',
  ERROR = 'ERROR',
}

export interface BleReaderBattery {
  level: number | null;
  isCharging: boolean;
  status: 'nominal' | 'low' | 'critical' | 'unknown';
}
// export interface ITerminalItem {
//   id: string;
//   name: string;
//   deviceTag: string;
//   locationId: string;
//   status?: string;
//   type?: string;
// }

// export interface IDeviceItem {
//   id: string;
//   name: string;
//   serialNumber: string;
//   lastConnected?: string;
//   terminal?: ITerminalItem | null;
//   // marks whether this is the primary device
//   primary?: boolean;
//   isActive: boolean;
// }

export interface ITerminalItem extends z.infer<typeof BusinessTerminalItemSchema> {
  status: string;
  type: string;
  providerLocationId?: string;
  /** Device Sync exposes this only for this device's assigned BlockChyp terminal. */
  localIpAddress?: string | null;
}

// Create standalone type that matches actual usage
// Note: serialNumber is optional because terminals can be assigned without it
export interface CurrentTerminal {
  id: string;
  name: string;
  deviceTag: string;
  executionMode: string;
  status: string;
  type: string;
  locationId?: string;
  providerLocationId?: string;
  /** Device Sync exposes this only for this device's assigned BlockChyp terminal. */
  localIpAddress?: string | null;
  serialNumber?: string; // Optional - matches actual usage
  provider: AllPaymentProvidersEnum; // Terminal's payment provider (e.g., BLOCKCHYP, TILLED, PAYFACTORY)
  providerTerminalId: string; // Provider-specific terminal ID
}

export interface TerminalConnectionState {
  status: TerminalConnectionStatus;
  lastConnected: Date | null;
  lastError: string | null;
}

// Override IDeviceItem to use CurrentTerminal instead of ITerminalItem for the terminal property
// This allows terminals to be assigned without serialNumber initially
export interface IDeviceItem extends Omit<z.infer<typeof BusinessDeviceItemSchema>, 'terminal'> {
  terminal?: CurrentTerminal;
}

const BleReaderBatterySchema = z.object({
  level: z.number().nullable(),
  isCharging: z.boolean(),
  status: z.enum(['nominal', 'low', 'critical', 'unknown']),
});

const TerminalConnectionStateSchema = z.object({
  status: z.nativeEnum(TerminalConnectionStatus),
  lastConnected: z.date().nullable(),
  lastError: z.string().nullable(),
});

// Update-related schemas
const UpdateHistoryItemSchema = z.object({
  version: z.string(),
  bundleId: z.string(),
  installedAt: z.date(),
  rollbackAvailable: z.boolean(),
  status: z.enum(['active', 'available', 'failed']),
});

const AvailableUpdateSchema = z.object({
  version: z.string(),
  bundleId: z.string(),
  url: z.string(),
  changelog: z.string().optional(),
  size: z.number().optional(),
});

// Update policy schemas
const GlobalUpdatePolicySchema = z.object({
  minRequiredVersion: z.string(),
  latestVersion: z.string().nullable(),
  blockedVersions: z.array(z.string()),
  lastUpdated: z.union([z.date(), z.string()]),
  versionsIndex: z
    .record(
      z.object({
        enabled: z.boolean(),
        releaseDate: z.union([z.date(), z.string()]),
        rollbackAllowed: z.boolean(),
        autoUpdate: z.boolean(),
      })
    )
    .optional(),
});

const BusinessUpdatePolicySchema = z.object({
  minRequiredVersion: z.string().nullable().optional(),
  blockedVersions: z.array(z.string()).optional(),
  latestVersion: z.string().nullable().optional(),
});

const MergedUpdatePolicySchema = z.object({
  minRequiredVersion: z.string(),
  latestVersion: z.string().nullable(),
  blockedVersions: z.array(z.string()),
  lastUpdated: z.union([z.date(), z.string()]),
  versionsIndex: z
    .record(
      z.object({
        enabled: z.boolean(),
        releaseDate: z.union([z.date(), z.string()]),
        rollbackAllowed: z.boolean(),
        autoUpdate: z.boolean(),
      })
    )
    .optional(),
  source: z.object({
    minRequiredVersion: z.enum(['business', 'global']),
    latestVersion: z.enum(['business', 'global']),
    blockedVersions: z.array(z.enum(['business', 'global'])),
  }),
});

const UpdatePolicyStateSchema = z.object({
  globalPolicy: GlobalUpdatePolicySchema.nullable(),
  businessPolicy: BusinessUpdatePolicySchema.nullable(),
  mergedPolicy: MergedUpdatePolicySchema.nullable(),
  loading: z.boolean(),
  error: z.string().nullable(),
  lastFetch: z.date().nullable(),
});

const FeatureFlagsSchema = z.object({
  elevateAiEnabled: z.boolean(),
});
type FeatureFlags = z.infer<typeof FeatureFlagsSchema>;

export const SystemStoreSchema = z.object({
  shouldRefetchPreload: z.boolean(),
  currentDeviceType: z.nativeEnum(DeviceType),
  baseUrl: z.string(),
  baseAthUrl: z.string(),
  basePublicUrl: z.string(),
  // Debug-only override of the main API base URL (null when using the build default)
  apiBaseUrlOverride: z.string().nullable(),
  appVersion: z.object({
    current: z.string(),
    minimum: z.string(),
    native: z.string().optional(), // Native app version
    nativeBuild: z.string().optional(), // OS build counter, independent from the app version
  }),
  featureFlags: FeatureFlagsSchema,
  appsToDisplayOnMobile: z.array(z.nativeEnum(AppNameEnum)),
  // Terminal connection state
  terminalConnection: TerminalConnectionStateSchema,
  // BLE Reader state
  bleReaderStatus: z.nativeEnum(BleReaderStatus),
  bleReader: z.any().nullable(), // ReaderInterface or null
  bleDiscoveredReaders: z.array(z.any()), // ReaderInterface[]
  bleReaderBattery: BleReaderBatterySchema,
  bleReaderError: z.string().nullable(),
  bleReaderUpdateProgress: z.number().nullable(),
  blePaymentStatus: z.nativeEnum(BlePaymentStatus),
  isPrinting: z.boolean(),
  // Update-related state
  updateStatus: z.enum(['idle', 'checking', 'available', 'downloading', 'installing', 'failed']),
  availableUpdate: AvailableUpdateSchema.nullable(),
  downloadProgress: z.number(), // 0-100
  updateHistory: z.array(UpdateHistoryItemSchema),
  lastUpdateCheck: z.date().nullable(),
  updateError: z.string().nullable(),
  // Update policy state
  updatePolicy: UpdatePolicyStateSchema,
  // Developer toggle for V4 payment page redirect
  useV4Payment: z.boolean(),
  // Payment page debug mode toggle (shows detailed sale/transaction info)
  showPaymentDebugMode: z.boolean(),
  // BlockChyp terminal debug log overlay toggle (V5 payment page console)
  showBlockchypDebugMode: z.boolean(),
  // Stripe M2 terminal debug log overlay toggle (V5 payment page console)
  showStripeM2DebugMode: z.boolean(),
  // Device-local outgoing API history capture toggle
  apiCallCaptureEnabled: z.boolean(),
  // Payment flow state (for Footer visibility on payment page)
  // Quick Pay panel open state (for v4 payment page footer button)
  v4QuickPayPanelOpen: z.boolean(),
  // Note: setter functions are typically not included in the Zod schema
});
export type UpdateHistoryItem = z.infer<typeof UpdateHistoryItemSchema>;
export type AvailableUpdate = z.infer<typeof AvailableUpdateSchema>;
export type UpdateStatus =
  | 'idle'
  | 'checking'
  | 'available'
  | 'downloading'
  | 'installing'
  | 'failed';

export type SystemStoreType = z.infer<typeof SystemStoreSchema> & {
  setResolvedAppVersion: (value: { current: string; native?: string; nativeBuild?: string }) => void;
  setMinimumAppVersion: (minimum: string) => void;
  setBaseUrl: (value: string) => void;
  setBaseAthUrl: (value: string) => void;
  setBasePublicUrl: (value: string) => void;
  // Debug-only API base URL override (persisted to localStorage)
  setApiBaseUrlOverride: (url: string) => void;
  clearApiBaseUrlOverride: () => void;
  setShouldRefetchPreload: (value: boolean) => void;
  setCurrentDeviceType: (value: DeviceType) => void;
  setAppsToDisplayOnMobile: (value: AppNameEnum[]) => void;
  // BLE Reader setters
  setBleReaderStatus: (value: BleReaderStatus) => void;
  setBleReader: (value: ReaderInterface | null) => void;
  setBleDiscoveredReaders: (value: ReaderInterface[]) => void;
  setBleReaderBattery: (value: BleReaderBattery) => void;
  setBleReaderError: (value: string | null) => void;
  setBleReaderUpdateProgress: (value: number | null) => void;
  setBlePaymentStatus: (value: BlePaymentStatus) => void;
  setIsPrinting: (value: boolean) => void;
  // Update-related setters
  setUpdateStatus: (value: UpdateStatus) => void;
  setAvailableUpdate: (value: AvailableUpdate | null) => void;
  setDownloadProgress: (value: number) => void;
  setUpdateHistory: (value: UpdateHistoryItem[]) => void;
  setLastUpdateCheck: (value: Date | null) => void;
  setUpdateError: (value: string | null) => void;
  addToUpdateHistory: (item: UpdateHistoryItem) => void;
  // Update policy setters
  setUpdatePolicy: (value: Partial<UpdatePolicyState>) => void;
  setGlobalPolicy: (value: GlobalUpdatePolicy | null) => void;
  setBusinessPolicy: (value: BusinessUpdatePolicy | null) => void;
  setMergedPolicy: (value: MergedUpdatePolicy | null) => void;
  setUpdatePolicyLoading: (value: boolean) => void;
  setUpdatePolicyError: (value: string | null) => void;
  setFeatureFlags: (value: Partial<FeatureFlags>) => void;
  setUseV4Payment: (value: boolean) => void;
  setShowPaymentDebugMode: (value: boolean) => void;
  setShowBlockchypDebugMode: (value: boolean) => void;
  setShowStripeM2DebugMode: (value: boolean) => void;
  setApiCallCaptureEnabled: (value: boolean) => void;
  setV4QuickPayPanelOpen: (value: boolean) => void;
  // Terminal connection setters
  setTerminalConnectionStatus: (status: TerminalConnectionStatus) => void;
  setTerminalConnectionLastConnected: (date: Date | null) => void;
  setTerminalConnectionError: (error: string | null) => void;
  resetTerminalConnection: () => void;
};
